import { Grid as MuiGrid, useMediaQuery } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { addSeconds, formatDistance } from "date-fns";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import {
  IStakingDeposit,
  IStakingRuleDepositSearchDto,
  IStakingRuleRewardSearchDto,
  StakingDepositStatus,
  StakingDepositTokenType,
  StakingRewardTokenType,
} from "@framework/types";
import { useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useGlobalDialogViewCallbacks } from "@framework/shared";
import { List } from "@dzambalaorg/ui-kit";

import { StakingRewardButton } from "../../../../../components/buttons";
import { MarketDialogKeys } from "../../../../../shared";
import { StakingDepositSearchForm } from "./search";

const STAKING_DEPOSITS_URL = "/staking/deposits";

const initialSearchValues = {
  stakingDepositStatus: [StakingDepositStatus.ACTIVE],
  contractIds: [],
  deposit: {
    tokenType: [] as Array<StakingDepositTokenType>,
  } as IStakingRuleDepositSearchDto,
  reward: {
    tokenType: [] as Array<StakingRewardTokenType>,
  } as IStakingRuleRewardSearchDto,
};

export const StakingDeposit = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount } = useFetchQuery<
    IPaginationResult<IStakingDeposit>
  >(STAKING_DEPOSITS_URL, { enableInfiniteScroll: isMd }, void 0, initialSearchValues);

  const { openViewDialog } = useGlobalDialogViewCallbacks<IStakingDeposit>();

  const onDepositView = (deposit: IStakingDeposit) => () => {
    openViewDialog(MarketDialogKeys.STAKING_DEPOSIT_VIEW, deposit, {
      message: "dialogs.view",
    });
  };

  return (
    <MuiGrid>
      <Breadcrumbs path={["dashboard", "staking", "staking.deposit"]} />

      <PageHeader message="pages.staking.deposit.title" />

      <StakingDepositSearchForm initialValues={initialSearchValues as any} />

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(deposit => (
            <List.Item key={deposit.id}>
              <List.ItemText sx={{ width: 0.6 }}>{deposit.stakingRule?.title}</List.ItemText>
              <List.ItemText sx={{ width: 0.4 }}>
                {deposit.startTimestamp
                  ? formatDistance(
                      addSeconds(new Date(deposit.startTimestamp), deposit.stakingRule?.durationAmount || 0),
                      Date.now(),
                      { addSuffix: true },
                    )
                  : ""}
              </List.ItemText>
              <ListActions>
                <StakingRewardButton stake={deposit} />
                <ListAction onClick={onDepositView(deposit)} message="form.tips.view" icon={Visibility} />
              </ListActions>
            </List.Item>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}
    </MuiGrid>
  );
};
